import { useEffect, useState } from "react";
import { getToken } from "../identity.js";
import { Button, ErrorPanel, Loading } from "../ui.jsx";
import { FlowStacked } from "../components/Logo.jsx";
import Gate from "./Gate.jsx";

const pendingKey = "flowbooks:oauth-pending";

export default function McpConnect({ user, onSignedIn }) {
  // Gate removes auth query strings after sign-in. Capture this request before
  // rendering Gate, and retain it across a refresh without storing credentials.
  const [query] = useState(() => {
    const current = window.location.search;
    if (current) sessionStorage.setItem(pendingKey, current);
    return current || sessionStorage.getItem(pendingKey) || "";
  });
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function consentRequest(approved) {
    const token = await getToken();
    const reading = approved === undefined;
    const response = await fetch(reading ? `/oauth/consent${query}` : "/oauth/consent", {
      method: reading ? "GET" : "POST",
      headers: { Authorization: `Bearer ${token}`, ...(!reading ? { "Content-Type": "application/json" } : {}) },
      ...(!reading ? { body: JSON.stringify({ ...Object.fromEntries(new URLSearchParams(query)), approved }) } : {}),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error_description || "Unable to connect Flow Books.");
    return data;
  }

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    consentRequest().then((data) => { if (!cancelled) setDetails(data); })
      .catch((err) => { if (!cancelled) setError(err); });
    return () => { cancelled = true; };
  }, [user, query]);

  async function choose(approved) {
    setBusy(true); setError(null);
    try {
      const result = await consentRequest(approved);
      sessionStorage.removeItem(pendingKey);
      window.location.assign(result.redirect_uri);
    } catch (err) { setError(err); setBusy(false); }
  }

  if (user === undefined) return <Loading label="Checking your session…" />;
  if (!user) return <Gate onSignedIn={onSignedIn} />;
  return <div className="gate"><div className="box">
    <div className="lock"><FlowStacked size={112} tone="light" /></div>
    <h2>Connect Flow Books</h2>
    {error && <ErrorPanel error={error} />}
    {!details && !error && <Loading label="Checking this connection…" />}
    {details && <>
      <p><strong>{details.client_name}</strong> is requesting read-only access to <strong>{details.organization_name}</strong>.</p>
      <p className="dim">Signed in as {details.user_name}.</p>
      <p>It can read clients, invoices, line items, payments, tracked time, contractors and incoming bills (including attached invoice PDFs) to prepare reports. It cannot create, edit, send, delete or pay invoices or bills.</p>
      <p className="sm dim">Invoice quantities of 1 are weekly rates. Quantities above 1 are hours.</p>
      <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
        <Button onClick={() => choose(true)} disabled={busy}>Allow read-only access</Button>
        <Button onClick={() => choose(false)} disabled={busy}>Cancel</Button>
      </div>
    </>}
  </div></div>;
}
